import type { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import type { MapTool } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { drawZoneUseCase } from "../../../application/map/useCases/drawZoneUseCase";
import { ZoneType } from "../../../domain/zone/types";
import type { AnyPolygonFeature } from "../../../domain/geometry/types";
import { ensureRingsClosed } from "../../helpers/JobMap/editHelpers";
import type { FeatureId } from "../engine/useMapDrawEngine";

export interface ZoneMetadata {
	zoneType: ZoneType;
	zoneName: string;
}

export interface DrawZoneTool extends MapTool {
	setZoneMetadata(meta: Partial<ZoneMetadata>): void;
	getZoneMetadata(): ZoneMetadata;
}

let zoneCounter = 0;

export function makeDrawZoneTool(deps: ToolDeps): DrawZoneTool {
	let jobId: string | null = null;
	let freehandActive = false;
	let metadata: ZoneMetadata = { zoneType: ZoneType.SAMPLE_ZONE, zoneName: "Zone" };

	function onFinish(_id: FeatureId, _ctx: { mode: string; action: string }) {
		if (!jobId) return;

		const snapshot = deps.drawEngine.getSnapshot();
		const drawn = snapshot.find((f) => f.geometry.type === "Polygon");
		if (!drawn) return;

		const drawnFeature: Feature<Polygon> = {
			type: "Feature",
			properties: {},
			geometry: {
				type: "Polygon",
				coordinates: ensureRingsClosed((drawn.geometry as Polygon).coordinates),
			},
		};

		const boundaryFC = deps.getBoundaryFC(jobId);
		if (!boundaryFC?.features?.length) {
			deps.showError("No boundary to validate zone against");
			return;
		}

		const boundary = mergeBoundaryFeatures(boundaryFC);
		const zoneId = `zone-${Date.now()}-${zoneCounter++}`;

		const existingFC: FeatureCollection = deps.getZonesFC(jobId) ?? {
			type: "FeatureCollection",
			features: [],
		};

		const result = drawZoneUseCase(
			{
				drawn: drawnFeature,
				zoneId,
				zoneName: metadata.zoneName,
				zoneType: metadata.zoneType,
				boundary,
			},
			existingFC,
			(fc) => deps.applyZones(jobId!, fc),
			(fc) => deps.applyZones(jobId!, fc),
		);

		if (!result.ok) {
			deps.showError(result.error);
			return;
		}

		deps.undoStack.push(result.data.command);
		deps.drawEngine.clear();
	}

	return {
		id: "draw-zone",
		get label() {
			return freehandActive ? "Zone (Freehand)" : "Draw Zone";
		},
		cursor: "crosshair",
		onToggleMode() {
			if (!freehandActive) {
				freehandActive = true;
				deps.drawEngine.clear();
				deps.drawEngine.setMode("freehand");
				deps.drawEngine.setFinishHandler(onFinish);
				return true;
			}
			return false;
		},
		onActivate() {
			jobId = deps.getSelectedJobId();
			if (!jobId) {
				deps.showError("No job selected");
				deps.deactivateTool();
				return;
			}

			const boundaryFC = deps.getBoundaryFC(jobId);
			if (!boundaryFC?.features?.length) {
				deps.showError("Draw a boundary first");
				deps.deactivateTool();
				return;
			}

			deps.drawEngine.setMode("polygon");
			deps.drawEngine.setFinishHandler(onFinish);
		},
		onDeactivate() {
			freehandActive = false;
			deps.drawEngine.setFinishHandler(null);
			deps.drawEngine.clear();
			deps.drawEngine.setMode("static");
			jobId = null;
		},
		setZoneMetadata(meta: Partial<ZoneMetadata>) {
			if (meta.zoneType !== undefined) metadata.zoneType = meta.zoneType;
			if (meta.zoneName !== undefined) metadata.zoneName = meta.zoneName;
		},
		getZoneMetadata() {
			return { ...metadata };
		},
	};
}

/** Merge all boundary FC features into a single Polygon or MultiPolygon feature */
function mergeBoundaryFeatures(fc: FeatureCollection): AnyPolygonFeature {
	if (fc.features.length === 1) {
		return fc.features[0] as AnyPolygonFeature;
	}
	// Collect all polygon rings from all features into one MultiPolygon
	const allCoords: Polygon["coordinates"][] = [];
	for (const f of fc.features) {
		const geom = (f as AnyPolygonFeature).geometry;
		if (geom.type === "Polygon") {
			allCoords.push(geom.coordinates);
		} else {
			allCoords.push(...(geom as MultiPolygon).coordinates);
		}
	}
	return {
		type: "Feature",
		properties: {},
		geometry: { type: "MultiPolygon", coordinates: allCoords },
	};
}
